import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Gift, Loader2, X } from "lucide-react";
import { sendQuizAbandonEvent } from "../../lib/pageviewTracker";

const GOLD = "#D49B3C";

/* Value-perception probes — one tap, no typing. */
const REASONS = [
  { slug: "unclear_value", label: "I'm not sure what I'd actually get" },
  { slug: "not_worth_effort", label: "The result isn't worth 20 questions" },
  { slug: "email_concern", label: "I don't want to give my email" },
  { slug: "not_now", label: "Just curious — not right now" },
  { slug: "other", label: "Something else" },
];

/**
 * Mid-quiz exit-intent rescue modal — the "2-job punch" plus save-my-spot.
 * Job 1: rescue — show real progress + the 2-free-months annual offer.
 * Job 2: if they still leave, capture a 1-tap value-perception reason.
 * Job 3 (save): email a magic link that restores the take later —
 *        `onSaveSpot(email)` is provided by Quiz.jsx and throws on failure.
 * Shown a limited number of times per visit (guarded by the caller).
 */
export default function ExitRescueModal({
  trigger, remaining, answered, total, questionIndex, onClose, onSaveSpot,
}) {
  const [view, setView] = useState("rescue"); // rescue | feedback | thanks | save | saved
  const [saveEmail, setSaveEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const sentRef = React.useRef(false);
  const navigate = useNavigate();
  const ctx = { trigger, question_index: questionIndex, answered, total };

  const sendOnce = (payload) => {
    if (sentRef.current) return;
    sentRef.current = true;
    sendQuizAbandonEvent(payload);
  };
  const keepGoing = () => {
    sendOnce({ action: "rescued", ...ctx });
    onClose();
  };
  const dismiss = () => {
    sendOnce({ action: "dismissed", ...ctx });
    onClose();
  };
  const pickReason = (slug) => {
    sendOnce({ action: "feedback", reason: slug, ...ctx });
    setView("thanks");
  };
  const submitSave = async (e) => {
    e.preventDefault();
    const email = saveEmail.trim();
    if (!email) {
      setSaveError("Email is required.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await onSaveSpot(email);
      setView("saved");
    } catch (err) {
      setSaveError(err?.message || "Something went wrong — try again?");
    } finally {
      setSaving(false);
    }
  };

  const pct = total ? Math.round((answered / total) * 100) : 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="quiz-exit-modal"
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-7 sm:p-9 text-center animate-in fade-in zoom-in-95 duration-300 relative">
        <button
          type="button"
          onClick={view === "thanks" || view === "saved" ? onClose : dismiss}
          className="absolute top-4 right-4 text-[#9b9892] hover:text-[#2C302E] transition-colors"
          aria-label="Close"
          data-testid="quiz-exit-close"
        >
          <X className="h-5 w-5" />
        </button>

        {view === "rescue" && (
          <>
            <span
              className="inline-block px-4 py-1.5 rounded-full text-[11px] font-semibold tracking-widest uppercase"
              style={{ background: "#FAEACB", color: "#A9712A" }}
            >
              Wait — you&apos;re close
            </span>
            <h2 className="font-serif text-3xl mt-4 text-[#1B2A3A] leading-tight">
              Only {remaining} question{remaining === 1 ? "" : "s"} between you
              and your MoneyMind.
            </h2>
            <div className="mt-5 flex items-center gap-3 text-xs text-[#787671]">
              <div className="flex-1 h-2 bg-[#E6E3DB] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${pct}%`, background: GOLD }}
                />
              </div>
              <span className="tabular-nums">{pct}% done</span>
            </div>
            <div
              className="mt-5 rounded-lg border border-[#EAD9AE] bg-[#FBF4E3] p-4 text-left flex items-start gap-3"
              data-testid="quiz-exit-offer"
            >
              <Gift className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "#A9712A" }} />
              <p className="text-sm text-[#4d4f4c] leading-relaxed">
                Finish today and lock in <strong>2 free months</strong> — sign
                up for the year and your <strong>first 60 days are free</strong>.
              </p>
            </div>
            <button
              type="button"
              onClick={keepGoing}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold transition-opacity hover:opacity-90"
              style={{ background: GOLD, color: "#2A1A05" }}
              data-testid="quiz-exit-keep-going"
            >
              Keep going — see my MoneyMind
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setView("save")}
              className="mt-4 block mx-auto text-sm text-[#2F6F8F] underline hover:text-[#2C302E] transition-colors"
              data-testid="quiz-exit-save-link"
            >
              Save my spot — email me a link to finish later
            </button>
            <button
              type="button"
              onClick={() => setView("feedback")}
              className="mt-3 text-sm text-[#9b9892] underline hover:text-[#2C302E] transition-colors"
              data-testid="quiz-exit-leave-anyway"
            >
              I still want to leave
            </button>
          </>
        )}

        {view === "feedback" && (
          <>
            <h2 className="font-serif text-2xl sm:text-3xl mt-2 text-[#1B2A3A] leading-tight">
              One tap before you go?
            </h2>
            <p className="mt-3 text-sm text-[#4d4f4c]">
              What made the quiz not feel worth finishing? Honest answers make
              it better.
            </p>
            <div className="mt-5 space-y-2.5">
              {REASONS.map((r) => (
                <button
                  key={r.slug}
                  type="button"
                  onClick={() => pickReason(r.slug)}
                  className="w-full text-left px-4 py-3 rounded-lg border border-[#E6E3DB] bg-[#FBFAF7] text-sm text-[#2C302E] transition-colors hover:border-[#D49B3C] hover:bg-[#FAEACB]"
                  data-testid={`quiz-exit-reason-${r.slug}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={dismiss}
              className="mt-4 text-sm text-[#9b9892] underline hover:text-[#2C302E] transition-colors"
              data-testid="quiz-exit-skip"
            >
              Skip
            </button>
          </>
        )}

        {view === "thanks" && (
          <div data-testid="quiz-exit-thanks">
            <span className="text-4xl" aria-hidden>🙏</span>
            <h2 className="font-serif text-2xl sm:text-3xl mt-3 text-[#1B2A3A]">
              Thanks — that genuinely helps.
            </h2>
            <p className="mt-3 text-sm text-[#4d4f4c]">
              Your answers so far are still here if you change your mind.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold transition-opacity hover:opacity-90"
              style={{ background: GOLD, color: "#2A1A05" }}
              data-testid="quiz-exit-resume"
            >
              Pick up where I left off
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setView("save")}
              className="mt-4 block mx-auto text-sm text-[#2F6F8F] underline hover:text-[#2C302E] transition-colors"
              data-testid="quiz-exit-thanks-save-link"
            >
              Email me a link to finish later
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="mt-3 text-sm text-[#9b9892] underline hover:text-[#2C302E] transition-colors"
              data-testid="quiz-exit-leave-now"
            >
              Leave for now
            </button>
          </div>
        )}

        {view === "save" && (
          <div data-testid="quiz-exit-save">
            <span className="text-4xl" aria-hidden>📌</span>
            <h2 className="font-serif text-2xl sm:text-3xl mt-3 text-[#1B2A3A] leading-tight">
              Save your spot
            </h2>
            <p className="mt-3 text-sm text-[#4d4f4c] leading-relaxed">
              {answered} of {total} answered. We&apos;ll email you a link that
              drops you back on the exact question you left — good for 14 days.
            </p>
            <form onSubmit={submitSave} className="mt-5 text-left">
              <input
                type="email"
                value={saveEmail}
                onChange={(e) => setSaveEmail(e.target.value)}
                required
                autoFocus
                placeholder="you@example.com"
                className="w-full px-3 py-2.5 border border-[#E6E3DB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D49B3C] focus:border-transparent"
                data-testid="quiz-exit-save-email"
              />
              {saveError && (
                <p
                  className="mt-3 p-2.5 text-sm bg-[#FFF6F1] border border-[#C05C3D] text-[#C05C3D] rounded"
                  data-testid="quiz-exit-save-error"
                >
                  {saveError}
                </p>
              )}
              <button
                type="submit"
                disabled={saving}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: GOLD, color: "#2A1A05" }}
                data-testid="quiz-exit-save-submit"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Email me my link
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
            <p className="mt-3 text-[11px] text-[#9b9892] leading-relaxed">
              One link, plus at most one reminder. No drip, no spam — your
              email isn&apos;t added to any list.
            </p>
            <button
              type="button"
              onClick={() => setView("rescue")}
              className="mt-3 text-sm text-[#9b9892] underline hover:text-[#2C302E] transition-colors"
              data-testid="quiz-exit-save-back"
            >
              Back
            </button>
          </div>
        )}

        {view === "saved" && (
          <div data-testid="quiz-exit-saved">
            <span className="text-4xl" aria-hidden>✉️</span>
            <h2 className="font-serif text-2xl sm:text-3xl mt-3 text-[#1B2A3A]">
              Link sent — check your inbox.
            </h2>
            <p className="mt-3 text-sm text-[#4d4f4c]">
              Your progress is saved. Finish now or later — your call.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold transition-opacity hover:opacity-90"
              style={{ background: GOLD, color: "#2A1A05" }}
              data-testid="quiz-exit-saved-continue"
            >
              Keep going now
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="mt-4 text-sm text-[#9b9892] underline hover:text-[#2C302E] transition-colors"
              data-testid="quiz-exit-saved-leave"
            >
              Leave for now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
